import { AnalyticsBrowser } from '@segment/analytics-next';

import { AnalyticsIdentity, AnalyticsPlugin, Properties } from '../analytics';
import { omitUndefined } from '../omit-undefined';

interface SegmentContext {
	platform: string;
}

interface SegmentConfig {
	apiKey: string;
	apiHost: string;
	/**
	 * Cookie domain for the Segment identity cookies. Leave unset on a surface that should keep
	 * the shared registrable-domain default; set it to `window.location.hostname` on a surface
	 * that must not share `ajs_user_id` with its siblings, which is why the booking portal sets
	 * it: the portal identifies the customer's own end users, the staff app identifies Cheqroom
	 * users, and one browser reaching both would otherwise swap identities in one jar.
	 */
	cookieDomain?: string;
}

export class Segment implements AnalyticsPlugin {
	private client: AnalyticsBrowser;
	private traits = {};
	private customIntegrations = {};

	private context: SegmentContext = {
		platform: 'Web',
	};

	constructor(private readonly config: SegmentConfig) {
		this.client = AnalyticsBrowser.load(
			{
				writeKey: config.apiKey,
			},
			{
				integrations: {
					'Segment.io': { apiHost: config.apiHost || 'api.segment.io/v1' },
				},
				retryQueue: true,
				...(config.cookieDomain && { cookie: { domain: config.cookieDomain } }),
			}
		);
	}

	get name(): string {
		return 'segment';
	}

	identify({ userId, ...traits }: AnalyticsIdentity): void {
		// analytics-next treats a blank id as "no new id": it keeps the stored one and replaces
		// its traits, so the payload would land on the previously identified user.
		if (!userId) return;

		// A blank workspace id is not a workspace. Dropping it here also keeps it out of
		// `this.traits`, which rides along on every later track call.
		this.traits = omitUndefined({ ...traits, workspaceId: traits.workspaceId || undefined });
		void this.client?.identify(userId, this.traits, {
			// A copy per call: analytics-next enriches the context it is handed in place, and the
			// singleton would then carry that page and campaign into every later event.
			context: { ...this.context },
		});

		// An empty id is not "no group" to analytics-next: it keeps the falsy id, skips persisting
		// it, and ships the traits against whichever group this browser last stored.
		if (!traits.workspaceId) return;

		void this.client?.group(
			traits.workspaceId,
			omitUndefined({
				name: traits.workspaceName,
				workspaceName: traits.workspaceName,
				industry: traits.industry,
				isSandbox: traits.isSandbox,
				renewalDate: traits.renewalDate,
				creationDate: traits.creationDate,
				conversionDate: traits.conversionDate,
				recurlyAccountCode: traits.recurlyAccountCode,
				// Only the owner may claim the field: every identified user used to overwrite it.
				ownerId: traits.isAccountOwner ? userId : undefined,
				enabledFeatures: traits.enabledFeatures,
				availableFeatures: traits.availableFeatures,
				featureFlags: traits.featureFlags,
				itemQuota: traits.itemQuota,
				itemQuotaPercentage: traits.itemQuotaPercentage,
			})
		);
	}

	initialize(): void {
		// init was done in constructor
	}

	page(): void {
		// Get the route name from the hash without the id. Global because portal routes are
		// workspace prefixed, and a lookahead rather than a captured separator: consuming the
		// trailing slash moves past it, so `/:workspaceId/:resourceId` left the second id raw.
		const route = window.location.pathname.replace(/\/\w{22}(?=$|\/)/g, '/:id');
		const name = route ? `${route}` : '/';

		void this.client?.page(undefined, name, {
			path: window.location.pathname + window.location.hash,
			url: window.location.toString(),
		});
	}

	track(event: string, source: string, props?: Properties): void {
		void this.client?.track(
			event,
			{
				...props,
				source,
			},
			{
				...this.context,
				traits: this.traits,
			}
		);
	}
}
